import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { clientApi } from '@/config/axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
export function DocumentExports({
  base,
  versionId,
  disabled = false,
}: {
  base: string;
  versionId?: string;
  disabled?: boolean;
}) {
  const [id, setId] = useState<string>(),
    [busy, setBusy] = useState(false);
  const { data, refetch } = useQuery<{ status: string; error_code?: string }>({
    queryKey: ['document-export', base, id],
    enabled: !!id,
    queryFn: async () => (await clientApi.get(`${base}/exports/${id}`)).data,
    refetchInterval: (query) => (['ready', 'failed'].includes(query.state.data?.status || '') ? false : 2000),
  });
  async function prepare() {
    setBusy(true);
    try {
      const r = await clientApi.post(`${base}/exports`, { version_id: versionId || null });
      if (id === r.data.id) await refetch();
      else setId(r.data.id);
    } catch {
      toast.error('Unable to prepare PDF. Check rendering configuration.');
    } finally {
      setBusy(false);
    }
  }
  async function open(print = false) {
    setBusy(true);
    const tab = print ? window.open('about:blank', '_blank') : null;
    try {
      const r = await clientApi.get(`${base}/exports/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      if (print) {
        if (tab) tab.location.href = url;
        else toast.error('Allow pop-ups to open the printable PDF.');
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = `${versionId ? 'report' : 'draft'}-${id}.pdf`;
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 300000);
    } catch {
      tab?.close();
      toast.error('Unable to retrieve verified PDF');
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button type="button" variant="outline" size="sm" disabled={busy || disabled} onClick={prepare}>
        {versionId ? 'Prepare PDF' : 'Prepare draft PDF'}
      </Button>
      {id && (
        <span role="status" className="text-sm text-muted-foreground">
          {data?.status === 'ready' ? 'PDF ready' : data?.status === 'failed' ? 'Rendering failed' : 'Preparing PDF…'}
        </span>
      )}
      {data?.status === 'failed' && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={async () => {
            try {
              await clientApi.post(`${base}/exports/${id}/retry`);
              await refetch();
            } catch {
              toast.error('Retry unavailable');
            }
          }}>
          Retry
        </Button>
      )}
      {data?.status === 'ready' && (
        <>
          <Button type="button" size="sm" disabled={busy} onClick={() => open()}>
            Save PDF
          </Button>
          <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => open(true)}>
            Open printable PDF
          </Button>
          <span className="text-sm text-muted-foreground">Use the PDF viewer’s Print action.</span>
        </>
      )}
    </div>
  );
}
